import { Editor } from '@toast-ui/react-editor'
import { useEffect, useRef, useState } from 'react'
import codeSyntaxHighlight from '@toast-ui/editor-plugin-code-syntax-highlight'
import colorSyntax from '@toast-ui/editor-plugin-color-syntax'
import '@toast-ui/editor/dist/i18n/ko-kr'
import '@toast-ui/editor/dist/toastui-editor.css'
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css'
import 'tui-color-picker/dist/tui-color-picker.css'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useAppSelector } from '@/redux/hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  fieldCheck,
  GolfBizNm,
  PromotionData,
  PromotionTitle,
  PromoType,
  thumbnailSizeInfo,
  ThumbnailType,
} from '../promotionItem'
import {
  buttonName,
  confirmMessages,
  errorMessages,
  successMessages,
  TInput,
} from '@/pages/board/boardItem'
import { Button, Select } from '@/components'
import './promotion.css'
import FileUpload from './fileUpload'
import { FileUploadResponse } from '@/pages/blog/blogItem'
import { PreviewIcon } from '@/pages/feedback/feedbackItem'
import { useResponsive } from '@/hooks/useResponsive'

type UpdateProps = {
  nttSn?: number
  nttSj?: string
  nttCn?: string
  prmtType?: string
  bizCd?: string
  prmtStartDate?: string
  prmtEndDate?: string
  prmtUrl?: string
  detailFileList: { fileSeq: number; fileName: string }[]
  nameList: GolfBizNm[]
  endDateUnlimited?: boolean
  thumbnail?: string
  thumbnailSeq?: number | null
  fileOrgNm?: string
  fileSeq?: number
}

type FormData = {
  nttSj: string
  nttCn: string
  prmtType: string
  prmtStartDate: string
  prmtEndDate: string
  thumbnail?: number | null
  thumbnailSeq?: number | null
}

export const UpdatePromotion: React.FC<UpdateProps> = ({
  nttSn,
  nttSj,
  nttCn,
  prmtType,
  bizCd,
  prmtStartDate,
  prmtEndDate,
  prmtUrl,
  detailFileList,
  nameList,
  endDateUnlimited,
  thumbnail,
  thumbnailSeq,
  fileOrgNm,
  fileSeq,
}) => {
  const editorRef = useRef<Editor>(null)
  const axios = useAxios()
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { showAlert, showConfirm } = useDialog()
  const navigate = useNavigate()
  //파일업로드 상태관리
  const [fileSeqList, setFileSeqList] = useState<number[]>([])
  const [fileNameList, setFileNameList] = useState<string[]>([])
  const [fileSourceList, setFileSourList] = useState<string[]>([])
  const [deleteSeqList, setDeleteSeqList] = useState<number[]>([])
  const [deleteNameList, setDeleteNameList] = useState<string[]>([])
  const [resetUploadTrigger, setResetUploadTrigger] = useState(false)
  const [isUnlimited, setIsUnlimited] = useState(endDateUnlimited)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  // 에디터 내 이미지와 파일 seq 매핑을 위한 상태 추가
  const imageInfoMapRef = useRef<
    Map<string, { width: number; height: number }>
  >(new Map())

  // 최종 저장용 HTML state (사이즈 정보 포함)
  const [finalNttCn, setFinalNttCn] = useState<string>('')
  const [editorImageMap, setEditorImageMap] = useState<
    Map<string, { seq: number; name: string }>
  >(new Map())
  //파일 선택 상태를 관리하는 state 추가
  const [hasUnuploadedFiles, setHasUnuploadedFiles] = useState(false)
  const [formData, setFormData] = useState<PromotionData>({
    cmpnyCd: '001',
    cstId: user?.cstId || '',
    nttSn: nttSn,
    regId: user?.cstId || '',
    regDate: '',
    updId: user?.cstId || '',
    updDate: '',
    prmtType: prmtType,
    bizCd: bizCd,
    nttSj: nttSj,
    nttCn: nttCn,
    prmtStartDate: prmtStartDate,
    prmtEndDate: prmtEndDate,
    prmtUrl: prmtUrl,
    page: 1,
    srchOpt: '',
    srchStr: '',
    fileSeqList: fileSeqList,
    fileNameList: fileNameList,
    fileSourceList: fileSourceList,
    deleteSeqList: deleteSeqList,
    thumbnail: thumbnailSeq && thumbnailSeq > 0 ? 'custom' : 'default',
    thumbnailSeq: thumbnailSeq,
  })

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  // 추가적으로, 에디터에서 키보드 이벤트로 이미지 삭제를 감지하는 방법
  useEffect(() => {
    if (editorRef.current) {
      const editorInstance = editorRef.current.getInstance()

      // 에디터에서 키 이벤트 감지 (Delete, Backspace)
      editorInstance.addHook('keydown', (keyEvent: KeyboardEvent) => {
        if (keyEvent.key === 'Delete' || keyEvent.key === 'Backspace') {
          // 키 이벤트 후 잠시 대기한 후 삭제된 이미지 체크
          setTimeout(() => {
            checkDeletedImagesInEditor()
          }, 100)
        }
      })

      // 에디터 내용이 직접 변경되었을 때도 체크
      editorInstance.addHook('change', () => {
        setTimeout(() => {
          checkDeletedImagesInEditor()
        }, 100)
      })
    }
  }, [editorImageMap, fileSeqList])

  // 5. formData 업데이트 useEffect 수정 (deleteNameList 제거)
  useEffect(() => {
    // console.log('🔄 [UpdatePromotion] formData 업데이트 useEffect 실행')
    // console.log('  - fileSeqList:', fileSeqList)
    // console.log('  - fileNameList:', fileNameList)
    // console.log('  - fileSourceList:', fileSourceList)
    // console.log('  - deleteSeqList:', deleteSeqList)

    setFormData((prevFormData) => {
      const newFormData = {
        ...prevFormData,
        fileSeqList: fileSeqList,
        fileNameList: fileNameList,
        fileSourceList: fileSourceList,
        deleteSeqList: deleteSeqList,
        // deleteNameList 제거
      }
      // console.log('📊 [UpdatePromotion] 업데이트된 formData:', newFormData)
      return newFormData
    })
  }, [fileSeqList, fileNameList, fileSourceList, deleteSeqList])

  // 컴포넌트 초기화 시 기존 이미지들의 크기 정보 파싱
  useEffect(() => {
    if (nttCn && editorRef.current) {
      // 기존 HTML에서 이미지들의 크기 정보 추출
      const parser = new DOMParser()
      const doc = parser.parseFromString(nttCn, 'text/html')
      const images = doc.querySelectorAll(
        'img[style*="width"], img[style*="height"]',
      )

      images.forEach((img) => {
        const src = img.getAttribute('src')
        const style = img.getAttribute('style')

        if (src && style) {
          // 스타일에서 width와 height 추출
          const widthMatch = style.match(/width:\s*(\d+)px/)
          const heightMatch = style.match(/height:\s*(\d+)px/)

          if (widthMatch && heightMatch) {
            const width = parseInt(widthMatch[1])
            const height = parseInt(heightMatch[1])

            // console.log(
            //   `[DEBUG] 기존 이미지 크기 정보 저장: ${src} - ${width}x${height}`,
            // )

            // 기존 이미지 정보를 imageInfoMapRef에 저장
            imageInfoMapRef.current.set(src, { width, height })
          }
        }
      })

      // console.log(
      //   '[DEBUG] 초기화 완료 - 저장된 이미지 정보:',
      //   Array.from(imageInfoMapRef.current.entries()),
      // )
    }
  }, [nttCn]) // nttCn이 변경될 때만 실행 (초기 로드 시)

  // useEffect(() => {
  //   //에디터 이미지 업로드
  //   if (editorRef.current) {
  //     editorRef.current
  //       .getInstance()
  //       .addHook(
  //         'addImageBlobHook',
  //         async (
  //           blob: Blob,
  //           callback: (url: string, altText: string) => void,
  //         ) => {
  //           const maxFileSize = 10 * 1024 * 1024 // 10MB

  //           if (blob.size > maxFileSize) {
  //             showAlert({
  //               title: '10MB 이상의 파일은 업로드 할 수 없어요.',
  //               message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
  //             })
  //             return
  //           }

  //           const { fileSeq, fileUrl } = await uploadImageToServer(blob)

  //           if (fileUrl) {
  //             // 먼저 이미지 URL을 ToastUI에 삽입하도록 callback을 사용
  //             callback(fileUrl, '')

  //             const editorInstance = editorRef.current?.getInstance()
  //             const currentHTML = editorInstance?.getHTML()

  //             if (!currentHTML) return

  //             let fixedHTML = currentHTML
  //               // 텍스트 + 이미지가 한 문단에 같이 있는 경우 분리
  //               .replace(
  //                 /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
  //                 (_, textBefore, imgTag, textAfter) => {
  //                   const parts = []
  //                   if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
  //                   parts.push(`<p>${imgTag}</p>`)
  //                   if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
  //                   return parts.join('')
  //                 },
  //               )

  //             // 마지막이 <img><br> 로 끝날 경우 개행용 <p> 추가
  //             if (fixedHTML.match(/<p><img[^>]+><br><\/p>$/)) {
  //               fixedHTML += '<p></p>'
  //             }

  //             editorInstance?.setHTML(fixedHTML)
  //           }
  //           return false
  //         },
  //       )
  //   }
  // }, [])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current
        .getInstance()
        .addHook(
          'addImageBlobHook',
          async (
            blob: Blob,
            callback: (url: string, altText: string) => void,
          ) => {
            const maxFileSize = 10 * 1024 * 1024 // 10MB

            if (blob.size > maxFileSize) {
              showAlert({
                title: '10MB 이상의 파일은 업로드 할 수 없어요.',
                message: `파일 크기가 ${(blob.size / (1024 * 1024)).toFixed(2)}MB 에요.`,
              })
              return
            }

            // 원본 이미지 크기 확인
            const checkImageSize = new Promise<{
              width: number
              height: number
            }>((resolve) => {
              const img = new Image()
              img.onload = () => {
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
              }
              img.src = URL.createObjectURL(blob)
            })

            const originalSize = await checkImageSize
            //console.log('[DEBUG] 새 이미지 원본 크기:', originalSize)

            const { fileSeq, fileUrl } = await uploadImageToServer(blob)

            if (fileUrl) {
              //console.log('[DEBUG] 새 이미지 URL 획득:', fileUrl)

              // 새 이미지 정보를 ref에 저장
              imageInfoMapRef.current.set(fileUrl, {
                width: originalSize.width,
                height: originalSize.height,
              })

              // console.log(
              //   '[DEBUG] 새 이미지 추가 후 imageInfoMapRef:',
              //   Array.from(imageInfoMapRef.current.entries()),
              // )

              // 1. 에디터 화면에는 일반 이미지로 표시 (사용자가 볼 수 있도록)
              callback(fileUrl, '')

              // 2. 짧은 지연 후 finalNttCn state 업데이트 (모든 이미지에 사이즈 정보 포함)
              setTimeout(() => {
                const editorInstance = editorRef.current?.getInstance()
                if (!editorInstance) return

                const currentHTML = editorInstance.getHTML()
                // console.log(
                //   '[DEBUG] 새 이미지 추가 후 현재 에디터 HTML:',
                //   currentHTML,
                // )

                // 모든 이미지에 스타일을 적용한 HTML 생성
                const styledHTML = applyImageStyles(currentHTML)

                //console.log('[DEBUG] 새 이미지 추가 후 최종 HTML:', styledHTML)
                setFinalNttCn(styledHTML)
              }, 100)
            }
            return false
          },
        )
    }
  }, [])

  const applyImageStyles = (html: string) => {
    // console.log('[DEBUG] applyImageStyles 시작')
    // console.log('[DEBUG] 입력 HTML:', html)
    // console.log(
    //   '[DEBUG] 현재 imageInfoMapRef:',
    //   Array.from(imageInfoMapRef.current.entries()),
    // )

    let styledHTML = html

    // 텍스트 + 이미지가 한 문단에 같이 있는 경우 분리
    styledHTML = styledHTML.replace(
      /<p>([^<]*?)?(<img[^>]+><br>)([^<]*)?<\/p>/g,
      (_, textBefore, imgTag, textAfter) => {
        const parts = []
        if (textBefore?.trim()) parts.push(`<p>${textBefore}</p>`)
        parts.push(`<p>${imgTag}</p>`)
        if (textAfter?.trim()) parts.push(`<p>${textAfter}</p>`)
        return parts.join('')
      },
    )

    // 마지막이 <img><br> 로 끝날 경우 개행용 <p> 추가
    if (styledHTML.match(/<p><img[^>]+><br><\/p>$/)) {
      styledHTML += '<p></p>'
    }

    // imageInfoMapRef에 있는 이미지들만 스타일 적용 (새로 업로드된 이미지들)
    imageInfoMapRef.current.forEach((sizeInfo, src) => {
      // console.log(`[DEBUG] 새 이미지 스타일 적용: ${src}`)
      // console.log(`[DEBUG] 사이즈 정보: ${sizeInfo.width}x${sizeInfo.height}`)

      // 해당 이미지가 현재 HTML에 있는지 확인하고, 스타일이 없는 경우만 적용
      const escapedSrc = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

      // 스타일이 이미 있는지 체크하는 정규식 (width나 height 스타일이 있으면 건너뜀)
      const hasStyleRegex = new RegExp(
        `<img[^>]*src="${escapedSrc}"[^>]*style="[^"]*(?:width|height)[^"]*"[^>]*>`,
        'g',
      )

      // 스타일이 없는 이미지만 찾는 정규식
      const noStyleRegex = new RegExp(
        `<img(?![^>]*style="[^"]*(?:width|height)[^"]*")([^>]*?)src="${escapedSrc}"([^>]*?)>`,
        'g',
      )

      // 이미 스타일이 있는 이미지는 건너뜀
      if (hasStyleRegex.test(styledHTML)) {
        //console.log(`[DEBUG] 이미 스타일이 적용된 이미지 건너뜀: ${src}`)
        return
      }

      // 스타일이 없는 이미지에만 스타일 적용
      const beforeReplace = styledHTML
      styledHTML = styledHTML.replace(noStyleRegex, (match) => {
        //console.log(`[DEBUG] 스타일 적용 대상: ${match}`)
        const result = `<img src="${src}" contenteditable="false" style="width: ${sizeInfo.width}px; height: ${sizeInfo.height}px;" />`
        //console.log(`[DEBUG] 스타일 적용 결과: ${result}`)
        return result
      })

      if (beforeReplace !== styledHTML) {
        //console.log(`[DEBUG] 새 이미지 스타일 적용 완료: ${src}`)
      } else {
        //console.log(`[DEBUG] 스타일 적용 실패 또는 불필요: ${src}`)
      }
    })

    //console.log('[DEBUG] applyImageStyles 최종 결과:', styledHTML)
    return styledHTML
  }

  // 에디터 내용 변경 핸들러
  const handleEditorChange = () => {
    const editorNttCn = editorRef.current?.getInstance()
    if (editorNttCn) {
      const nttCn = editorNttCn.getHTML()
      setFormData((prev) => ({ ...prev, nttCn }))

      // 에디터 내용이 변경될 때마다 삭제된 이미지 체크
      setTimeout(() => {
        checkDeletedImagesInEditor()
      }, 100)
    }
  }

  const handleUploadedFile = (
    uploadSeqs: number[],
    uploadNames: string[],
    uploadSources: string[],
  ) => {
    // console.log('🎯 [UpdatePromotion] handleUploadedFile 시작')
    // console.log('  - 받은 uploadSeqs:', uploadSeqs, 'type:', typeof uploadSeqs)
    // console.log(
    //   '  - 받은 uploadNames:',
    //   uploadNames,
    //   'type:',
    //   typeof uploadNames,
    // )
    // console.log(
    //   '  - 받은 uploadSources:',
    //   uploadSources,
    //   'type:',
    //   typeof uploadSources,
    // )
    // console.log('  - 기존 fileSeqList:', fileSeqList)
    // console.log('  - 기존 fileNameList:', fileNameList)
    // console.log('  - 기존 fileSourceList:', fileSourceList)

    setFileSeqList((prev) => {
      const newList = [...prev, ...uploadSeqs]
      return newList
    })

    setFileNameList((prev) => {
      const newList = [...prev, ...uploadNames]
      return newList
    })

    setFileSourList((prev) => {
      const newList = [...prev, ...uploadSources]
      return newList
    })

    setHasUnuploadedFiles(false)
  }

  const handleDeletedFile = (fileSeqs: number[]) => {
    // console.log('🗑️ [UpdatePromotion] handleDeletedFile 호출')
    // console.log('  - 받은 fileSeqs:', fileSeqs)
    // console.log('  - 현재 deleteSeqList (변경 전):', deleteSeqList)
    // console.log('  - 현재 fileSeqList (변경 전):', fileSeqList)
    // console.log('  - 현재 fileNameList (변경 전):', fileNameList)
    // console.log('  - 현재 formData.thumbnailSeq:', formData.thumbnailSeq)

    // 삭제 리스트 업데이트 - 받은 값으로 전체 교체 (중복 제거)
    const newDeleteSeqList = Array.from(new Set(fileSeqs)) // 중복 제거
    setDeleteSeqList(newDeleteSeqList)

    // 만약 삭제되는 파일 중에 현재 thumbnailSeq가 포함되어 있다면
    if (formData.thumbnailSeq && fileSeqs.includes(formData.thumbnailSeq)) {
      setFormData((prev) => ({
        ...prev,
        thumbnailSeq: null,
      }))
    }

    // fileSeqList에서 삭제된 파일들 제거
    setFileSeqList((prev) => {
      const filtered = prev.filter((seq) => !fileSeqs.includes(seq))
      return filtered
    })

    // fileNameList에서도 해당 인덱스의 파일명들 제거
    setFileNameList((prev) => {
      const newNames = prev.filter((name, index) => {
        const currentSeq = fileSeqList[index]
        const shouldKeep = !fileSeqs.includes(currentSeq)
        return shouldKeep
      })

      return newNames
    })

    // fileSourceList에서도 해당 인덱스의 소스들 제거
    setFileSourList((prev) => {
      const newSources = prev.filter((source, index) => {
        const currentSeq = fileSeqList[index]
        const shouldKeep = !fileSeqs.includes(currentSeq)
        return shouldKeep
      })

      return newSources
    })

    setHasUnuploadedFiles(false)
  }

  // 기존 uploadImageToServer 함수 수정
  const uploadImageToServer = async (
    blob: Blob,
  ): Promise<{
    fileUrl: string
    fileSeq: number | null
    fileSource: string
  }> => {
    const formData = new FormData()
    formData.append('file', blob)

    try {
      const response = await axios.post<{ data: FileUploadResponse }>(
        '/feedback/fileUpload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )

      const fileUrl =
        process.env.REACT_APP_BASE_URL + '/upload/' + response.data.data.fileNm
      const fileSeq = response.data.data.fileSeq
      const fileName = response.data.data.fileNm
      const fileSource = 'EDITOR'

      // 에디터 이미지 매핑 정보 저장 (URL을 키로 사용)
      setEditorImageMap((prev) => {
        const newMap = new Map(prev)
        newMap.set(fileUrl, { seq: fileSeq, name: fileName })
        return newMap
      })

      setFileSeqList((prev) => [...prev, fileSeq])
      setFileNameList((prev) => [...prev, fileName])
      setFileSourList((prev) => [...prev, fileSource])

      return { fileUrl, fileSeq, fileSource }
    } catch (error) {
      showAlert({
        title: '이미지 업로드 실패',
        message: error,
        btnName: '확인',
      })
      return { fileUrl: '', fileSeq: null, fileSource: '' }
    }
  }

  // 에디터 내용 변경 감지하여 삭제된 이미지 찾기
  const checkDeletedImagesInEditor = () => {
    if (!editorRef.current) return

    const currentHTML = editorRef.current.getInstance().getHTML()
    const currentImageUrls = new Set<string>()

    // 현재 에디터에 있는 이미지 URL들 추출
    const imgRegex = /<img[^>]+src="([^"]+)"/g
    let match
    while ((match = imgRegex.exec(currentHTML)) !== null) {
      currentImageUrls.add(match[1])
    }

    // 매핑된 이미지 중에서 현재 에디터에 없는 것들 찾기
    const deletedImages: { seq: number; name: string }[] = []

    editorImageMap.forEach((imageInfo, imageUrl) => {
      if (!currentImageUrls.has(imageUrl)) {
        deletedImages.push(imageInfo)
      }
    })

    if (deletedImages.length > 0) {
      // 삭제된 이미지들을 삭제 목록에 추가
      const deletedSeqs = deletedImages.map((img) => img.seq)
      const deletedNames = deletedImages.map((img) => img.name)

      setDeleteSeqList((prev) => {
        const newList = Array.from(new Set([...prev, ...deletedSeqs])) // 중복 제거
        return newList
      })

      setDeleteNameList((prev) => {
        const newList = Array.from(new Set([...prev, ...deletedNames])) // 중복 제거
        return newList
      })

      // fileSeqList와 fileNameList에서도 제거
      setFileSeqList((prev) => {
        const filtered = prev.filter((seq) => !deletedSeqs.includes(seq))
        return filtered
      })

      setFileNameList((prev) => {
        const filtered = prev.filter((name) => !deletedNames.includes(name))
        return filtered
      })

      setFileSourList((prev) => {
        const newSources = prev.filter((source, index) => {
          const currentSeq = fileSeqList[index]
          return !deletedSeqs.includes(currentSeq)
        })
        return newSources
      })

      // 매핑에서도 삭제된 이미지들 제거
      setEditorImageMap((prev) => {
        const newMap = new Map(prev)
        deletedImages.forEach((img) => {
          // URL을 키로 찾아서 삭제
          for (const [url, info] of newMap.entries()) {
            if (info.seq === img.seq) {
              newMap.delete(url)
              break
            }
          }
        })
        return newMap
      })
    }
  }

  // 입력된 html 정보에서 img url 추출
  // const getImagesFromEditor = (editorInstance: any) => {
  //   const htmlContent = editorInstance.getHTML() // 에디터의 HTML 콘텐츠 가져오기
  //   const imgUrls = Array.from(
  //     new DOMParser()
  //       .parseFromString(htmlContent, 'text/html')
  //       .querySelectorAll('img'),
  //   ).map((img) => {
  //     const imgSrc = img.src
  //     const filePath = imgSrc.split('upload')[1]
  //     return decodeURIComponent(filePath)
  //   })

  //   return imgUrls
  // }

  // 기존 이미지 중 삭제해야 할 이미지 찾기
  // const compareAndDeleteImages = (
  //   oldFileName: string[],
  //   newFileName: string[],
  // ) => {
  //   // newFileName에서 파일명만 추출
  //   const newFileNamesWithoutPath = newFileName.map((filePath) => {
  //     return filePath.split('/').pop() // 경로에서 파일명만 추출
  //   })

  //   // oldFileName과 비교하여 삭제할 이미지 추출
  //   const imagesToDelete = oldFileName.filter(
  //     (fileNm) => !newFileNamesWithoutPath.includes(fileNm),
  //   )

  //   // 삭제할 이미지들의 seq를 찾기
  //   const deleteImgSeqs = imagesToDelete
  //     .map((fileName) => {
  //       const file = detailFileList.find((file) => file.fileName === fileName)
  //       return file ? file.fileSeq : null // fileSeq가 없으면 null
  //     })
  //     .filter((seq): seq is number => seq !== null) // 타입 가드 사용

  //   return deleteImgSeqs
  // }

  const handleChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement

    // 체크박스 처리 (종료일 미지정)
    if (type === 'checkbox' && name === 'unlimited' && value === 'unlimited') {
      setIsUnlimited(checked)

      if (checked) {
        setFormData((prev) => ({ ...prev, prmtEndDate: '2099-12-31' }))
        showAlert({
          title: '종료일을 미지정으로 선택했어요.',
          message: '미지정시 게시물이 계속 노출돼요.',
        })
      } else {
        // 체크 해제 시 종료일 초기화 (또는 기본값으로 설정)
        setFormData((prev) => ({ ...prev, prmtEndDate: '' }))
      }
      return // 체크박스는 여기서 끝
    }

    if (name === 'prmtStartDate') {
      if (
        formData.prmtEndDate &&
        new Date(value) > new Date(formData.prmtEndDate)
      ) {
        showAlert({
          type: 'error',
          title: '시작일은 종료일 이전이어야 해요.',
        })
        return
      }
    }

    // 종료일에 대한 검증 추가
    if (name === 'prmtEndDate') {
      if (
        formData.prmtStartDate &&
        new Date(value) < new Date(formData.prmtStartDate)
      ) {
        showAlert({
          type: 'error',
          title: '종료일은 시작일 이후여야 해요.',
        })
        return
      }

      // 종료일을 선택했으면 isUnlimited 체크박스 해제
      if (isUnlimited) {
        setIsUnlimited(false)
      }
    }

    if (name === 'thumbnail') {
      const selectedValue = value
      if (selectedValue !== 'custom') {
        setResetUploadTrigger(true)
        // custom이 아닌 경우 thumbnailSeq를 null로 설정
        setFormData((prev) => ({
          ...prev,
          thumbnail: selectedValue,
          thumbnailSeq: null,
        }))
      } else {
        // custom일 경우 상태 false로 초기화
        setResetUploadTrigger(false)
        setFormData((prev) => ({
          ...prev,
          thumbnail: selectedValue,
        }))
      }
      return // 중복 실행 방지
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleModalAction = (type: 'update' | 'cancel') => {
    if (type === 'update') {
      const requiredFields: { field: keyof FormData; message: string }[] = [
        { field: 'nttSj', message: fieldCheck.nttSj },
        { field: 'nttCn', message: fieldCheck.nttCn },
        { field: 'prmtType', message: fieldCheck.prmtType },
        { field: 'prmtStartDate', message: fieldCheck.prmtStartDate },
        { field: 'prmtEndDate', message: fieldCheck.prmtEndDate },
        // { field: 'thumbnail', message: fieldCheck.thumbnail },
      ]

      for (const { field, message } of requiredFields) {
        const value = formData[field]

        if (field === 'nttCn') {
          const nttCnvalue = formData.nttCn
          // nttCn이 HTML 형태일 경우, 이미지 태그는 허용하고 나머지 태그는 제거
          const strippedText = nttCnvalue
            ?.replace(/<(?!img\s*[^>]*>)[^>]+>/g, ' ')
            .trim() // img 태그를 제외한 나머지 HTML 태그 제거

          // 내용이 없거나 공백만 있는 경우
          if (
            strippedText === '' ||
            strippedText?.replace(/\s+/g, '').length === 0
          ) {
            showAlert({
              type: 'warning',
              title: message,
            })
            return
          }
        } else if (typeof value === 'string' && !value.trim()) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }

        if (field === 'thumbnail') {
          const type = formData.thumbnail
          if (type === 'custom' && !formData.thumbnailSeq) {
            showAlert({
              type: 'warning',
              title: message,
            })
            return
          }
        }

        if (!formData[field]) {
          showAlert({
            type: 'warning',
            title: message,
          })
          return
        }
      }

      // 업로드되지 않은 파일이 있는지 확인
      if (hasUnuploadedFiles) {
        showAlert({
          type: 'warning',
          title: '선택된 파일을 먼저 업로드해주세요.',
        })
        return
      }

      showConfirm({
        title: confirmMessages.insert,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          updatePromo()
        },
      })
    }

    if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(`/promotion`, { state: location.state }) // 500ms 후에 페이지 이동
          }, 100)
        },
      })
    }
  }

  const updatePromo = async () => {
    // console.log('🚀 [UpdatePromotion] updatePromo 함수 실행 시작')
    // console.log('  - 현재 formData:', formData)
    // console.log('  - deleteSeqList:', deleteSeqList)
    // console.log('  - 기존 thumbnailSeq (props):', thumbnailSeq)
    // console.log('  - 현재 thumbnailSeq (formData):', formData.thumbnailSeq)

    // 썸네일 처리 로직
    let finalDeleteSeqList = [...deleteSeqList]

    if (formData.thumbnail === 'default') {
      // 이미지 없음을 선택한 경우
      formData.thumbnailSeq = null

      // 기존에 썸네일이 있었다면 삭제 목록에 추가
      if (thumbnailSeq && thumbnailSeq > 0) {
        if (!finalDeleteSeqList.includes(thumbnailSeq)) {
          finalDeleteSeqList.push(thumbnailSeq)
        }
      }
    } else if (formData.thumbnail === 'custom') {
      // 커스텀 이미지를 선택한 경우
      if (formData.thumbnailSeq && formData.thumbnailSeq !== thumbnailSeq) {
        // 새로운 썸네일이 업로드되었고, 기존 썸네일과 다른 경우
        if (
          thumbnailSeq &&
          thumbnailSeq > 0 &&
          !finalDeleteSeqList.includes(thumbnailSeq)
        ) {
          finalDeleteSeqList.push(thumbnailSeq)
        }
      } else if (!formData.thumbnailSeq || formData.thumbnailSeq === null) {
        // 파일을 업로드했다가 삭제한 경우 - 기존 썸네일로 되돌림
        formData.thumbnailSeq = thumbnailSeq
      }
    }

    // 중복 제거
    finalDeleteSeqList = Array.from(new Set(finalDeleteSeqList))

    const updateData = {
      ...formData,
      nttCn: finalNttCn || formData.nttCn,
      deleteSeqList: finalDeleteSeqList,
      deleteNameList: deleteNameList,
    }

    // console.log('📤 [UpdatePromotion] 서버로 전송할 updateData:', updateData)
    // console.log('  - 최종 deleteSeqList:', finalDeleteSeqList)
    // console.log('  - 최종 thumbnailSeq:', updateData.thumbnailSeq)

    try {
      const response = await axios.post(`/promotion/update`, updateData)

      showAlert({
        type: 'success',
        title: successMessages.update,
        btnName: buttonName.confirm,
        onClose() {
          setTimeout(() => {
            navigate(0)
          }, 100)
        },
      })
    } catch (err: any) {
      showAlert({
        type: 'error',
        title: errorMessages.update,
        message: err.response?.data?.message,
        btnName: buttonName.confirm,
      })
    }
  }

  const handleFileSelectionChange = (hasSelectedFiles: boolean) => {
    setHasUnuploadedFiles(hasSelectedFiles)
  }

  const handleThumbnailUpload = (fileSeq: number) => {
    setFormData((prev) => ({
      ...prev,
      thumbnailSeq: fileSeq,
      // thumbnail이 custom인 상태에서 파일이 업로드되면 thumbnailSeq 설정
    }))
  }

  return (
    <>
      {!isMobile ? (
        <>
          <div className="promo-header">
            <div className="promo-title">{PromotionTitle}</div>
          </div>
          <div className="edit-container">
            <div className="edit-group">
              <label className="required-label">구분</label>
              <div className="radio-group">
                {PromoType.filter((type) => type.value !== '00').map((type) => (
                  <label key={type.value}>
                    <input
                      type="radio"
                      name="prmtType"
                      value={type.value}
                      checked={formData.prmtType === type.value} // formData.prmtType과 비교하여 선택 상태 결정
                      onChange={handleChange}
                    />
                    {type.label}
                  </label>
                ))}
                <Select
                  id="bizCd"
                  name="bizCd"
                  width="100px"
                  className={`golf-select ${formData.prmtType === '01' ? 'visible' : 'hidden'}`}
                  value={formData.bizCd}
                  onChange={handleChange}
                >
                  {nameList.map((option, index) => (
                    <option
                      key={`${option.areaCd}-${index}`}
                      value={option.bizCd}
                    >
                      {option.bizNm}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="edit-group">
              <label className="required-label">프로모션 기간</label>
              <div className="date-group">
                <input
                  type="date"
                  name="prmtStartDate"
                  value={formData?.prmtStartDate}
                  onChange={handleChange}
                />
                ~
                {isUnlimited ? (
                  <div className="unlimited">
                    종료일을 미지정으로 선택했어요.
                  </div>
                ) : (
                  <input
                    type="date"
                    name="prmtEndDate"
                    value={formData.prmtEndDate}
                    onChange={handleChange}
                  />
                )}
                <div className="radio-group">
                  <label>
                    <input
                      type="checkbox"
                      name="unlimited"
                      value="unlimited"
                      checked={isUnlimited}
                      onChange={handleChange}
                    />
                    종료일 미지정
                  </label>
                </div>
              </div>
            </div>

            <div className="edit-group">
              <label className="required-label">프로모션 명</label>
              <div
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  gap: '5px',
                }}
              >
                <input
                  type="text"
                  name="nttSj"
                  maxLength={255}
                  value={formData.nttSj}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="edit-group">
              <label>프로모션 링크</label>
              <input
                type="text"
                name="prmtUrl"
                maxLength={255}
                value={formData.prmtUrl}
                onChange={handleChange}
              />
            </div>
            <div className="edit-group">
              <label className="required-label">
                대표 이미지
                <span className="hint-text">{thumbnailSizeInfo}</span>
              </label>
              <div className="radio-group">
                {ThumbnailType.map((type) => (
                  <label key={type.value}>
                    <input
                      type="radio"
                      name="thumbnail"
                      value={type.value}
                      checked={formData.thumbnail === type.value}
                      onChange={handleChange}
                    />
                    {type.label}
                    {/* 'custom' 타입일 때만 FileUpload 컴포넌트 렌더링 */}
                    {type.value === 'custom' && (
                      <div className="upload-wrapper">
                        <FileUpload
                          onDeletedFile={handleDeletedFile}
                          onUploadedFile={handleUploadedFile}
                          onFileSelectionChange={handleFileSelectionChange} // 새로 추가
                          onUploadComplete={handleThumbnailUpload} // 이 부분 추가
                          resetUploadTrigger={resetUploadTrigger}
                          fileOrgName={fileOrgNm}
                          fileSeq={fileSeq}
                        />
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {fileOrgNm && !resetUploadTrigger && (
                <>
                  <div
                    key={thumbnailSeq}
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      justifyItems: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <PreviewIcon />
                    <span style={{ color: '#979797' }}>등록된 파일명 :</span>
                    <span style={{ color: '#979797' }}>&nbsp;{fileOrgNm}</span>
                  </div>
                </>
              )}
            </div>
            <Editor
              ref={editorRef}
              initialValue={formData.nttCn}
              useCommandShortcut={true}
              previewStyle="vertical" // 미리보기 스타일 지정
              placeholder="내용을 입력해 주세요!"
              height="500px" // 에디터 창 높이
              initialEditType="wysiwyg" // 초기 입력모드 설정 (기본 markdown)
              language="ko-KR"
              hideModeSwitch={true} // 하단의 타입 선택 탭 숨기기
              plugins={[colorSyntax, codeSyntaxHighlight]}
              onChange={handleEditorChange}
            />
          </div>
          <div className="promo-button">
            <Button color="neverbe" onClick={() => handleModalAction('update')}>
              저장
            </Button>
            <Button color="neverbe" onClick={() => handleModalAction('cancel')}>
              취소
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="m-edit-container">
            <div className="m-edit-header">
              <h1 className="m-edit-title">정보</h1>
              <button
                className="blog-back-btn"
                onClick={() => handleModalAction('cancel')}
              >
                ← 뒤로
              </button>
            </div>
            <div className="m-edit-form">
              <div className="m-edit-group">
                <label className="m-label m-required-label">구분</label>
                <div className="radio-group">
                  {PromoType.filter((type) => type.value !== '00').map(
                    (type) => (
                      <label key={type.value}>
                        <input
                          type="radio"
                          name="prmtType"
                          value={type.value}
                          checked={formData.prmtType === type.value} // formData.prmtType과 비교하여 선택 상태 결정
                          onChange={handleChange}
                        />
                        {type.label}
                      </label>
                    ),
                  )}
                  <Select
                    id="bizCd"
                    name="bizCd"
                    width="100px"
                    className={`golf-select ${formData.prmtType === '01' ? 'visible' : 'hidden'}`}
                    value={formData.bizCd}
                    onChange={handleChange}
                  >
                    {nameList.map((option, index) => (
                      <option
                        key={`${option.areaCd}-${index}`}
                        value={option.bizCd}
                      >
                        {option.bizNm}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="m-edit-group">
                <div className="radio-group">
                  <label className="m-label m-required-label">
                    프로모션 기간
                  </label>
                  <label
                    className="m-radio-label"
                    style={{ fontWeight: 'normal', fontSize: '12px' }}
                  >
                    <input
                      type="checkbox"
                      name="unlimited"
                      value="unlimited"
                      checked={isUnlimited}
                      onChange={handleChange}
                    />
                    종료일 미지정
                  </label>
                </div>
                <div className="date-group">
                  <input
                    type="date"
                    name="prmtStartDate"
                    style={{ width: '150px' }}
                    value={formData?.prmtStartDate}
                    onChange={handleChange}
                  />
                  ~
                  {isUnlimited ? (
                    <div className="unlimited">종료일 미지정</div>
                  ) : (
                    <input
                      type="date"
                      name="prmtEndDate"
                      style={{ width: '150px' }}
                      value={formData.prmtEndDate}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </div>

              <div className="m-edit-group">
                <label className="m-label m-required-label">프로모션 명</label>
                <div style={{ marginTop: '10px' }} />
                <input
                  type="text"
                  name="nttSj"
                  maxLength={255}
                  value={formData.nttSj}
                  onChange={handleChange}
                />
              </div>

              <div className="m-edit-group">
                <label className="m-label">프로모션 링크</label>
                <div style={{ marginTop: '10px' }} />
                <input
                  type="text"
                  name="prmtUrl"
                  maxLength={255}
                  value={formData.prmtUrl}
                  onChange={handleChange}
                />
              </div>
              <div className="m-edit-group" style={{ marginBottom: '0px' }}>
                <label className="m-label m-required-label">
                  대표 이미지
                  <span className="m-hint-text">{thumbnailSizeInfo}</span>
                </label>
                <div className="m-radio-group">
                  {ThumbnailType.map((type) => (
                    <label className="m-radio-label" key={type.value}>
                      <div className="m-radio-content">
                        <div className="m-radio-text-line">
                          <input
                            className="m-radio-input"
                            type="radio"
                            name="thumbnail"
                            value={type.value}
                            checked={formData.thumbnail === type.value}
                            onChange={handleChange}
                          />
                          <span>{type.label}</span>
                          {/* 파일명을 라벨 바로 옆에 표시 */}
                          {type.value === 'custom' &&
                            fileOrgNm &&
                            !resetUploadTrigger && (
                              <div className="m-file-name" key={thumbnailSeq}>
                                <PreviewIcon />
                                <span className="m-file-name-text">
                                  {fileOrgNm}
                                </span>
                              </div>
                            )}
                        </div>

                        {/* FileUpload 컴포넌트를 라벨 아래에 배치 */}
                        {type.value === 'custom' && (
                          <div className="m-upload-wrapper">
                            <FileUpload
                              onDeletedFile={handleDeletedFile}
                              onUploadedFile={handleUploadedFile}
                              onFileSelectionChange={handleFileSelectionChange}
                              onUploadComplete={handleThumbnailUpload}
                              resetUploadTrigger={resetUploadTrigger}
                              fileOrgName={fileOrgNm}
                              fileSeq={fileSeq}
                            />
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <Editor
                ref={editorRef}
                initialValue={formData.nttCn}
                useCommandShortcut={true}
                previewStyle="vertical" // 미리보기 스타일 지정
                placeholder="내용을 입력해 주세요!"
                height="500px" // 에디터 창 높이
                initialEditType="wysiwyg" // 초기 입력모드 설정 (기본 markdown)
                language="ko-KR"
                hideModeSwitch={true} // 하단의 타입 선택 탭 숨기기
                plugins={[colorSyntax, codeSyntaxHighlight]}
                onChange={handleEditorChange}
              />
              <div className="mobile-post-actions">
                <button
                  type="button"
                  className="mobile-action-btn primary"
                  onClick={() => handleModalAction('update')}
                >
                  저장
                </button>
                <button
                  type="button"
                  className="mobile-action-btn user"
                  onClick={() => handleModalAction('cancel')}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
